import * as THREE from 'three';
import { TILE_SIZE } from '../labyrinth/Labyrinth';
import type { Entity, Enemy } from '../labyrinth/Entity';
import type { SwarmManager } from '../labyrinth/SwarmManager';

const RADAR_BLIP_DURATION = 5.0;
const BLOB_RADIUS = TILE_SIZE * 0.7;
const QUAD_SIZE   = BLOB_RADIUS * 4; // covers the outer haze (radius * 2 → diameter * 2)
const Y_OFFSET    = 0.22;

/** Radial gradient — three stops mirror the 3-layer 2D blobs. */
function makeBlobTexture(): THREE.CanvasTexture {
  const px = 128;
  const c = document.createElement('canvas');
  c.width = c.height = px;
  const ctx = c.getContext('2d')!;
  const grad = ctx.createRadialGradient(px / 2, px / 2, 0, px / 2, px / 2, px / 2);
  // 0.4 radius (core), 1.0 radius (mid), 2.0 radius (outer haze) — same ratios as 2D
  grad.addColorStop(0,    'rgba(180,255,200,0.85)'); // bright core
  grad.addColorStop(0.20, 'rgba(120,255,160,0.45)');
  grad.addColorStop(0.50, 'rgba(  0,255, 80,0.20)'); // mid glow
  grad.addColorStop(1,    'rgba(  0,255, 80,0.00)'); // outer haze fades to transparent
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, px, px);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

interface BlobMesh {
  mesh: THREE.Mesh;
  material: THREE.MeshBasicMaterial;
}

/**
 * Faint green radar blobs for enemies that have pinged the player but are
 * outside line-of-sight (visAlpha === 0). Mirrors LabyrinthState.drawRadarBlobs
 * — alpha = (1 - t²) where t = elapsed/RADAR_BLIP_DURATION.
 */
export class RadarBlobs3D {
  readonly group = new THREE.Group();
  private readonly geometry: THREE.PlaneGeometry;
  private readonly texture: THREE.CanvasTexture;
  private readonly pool: BlobMesh[] = [];
  private active = 0;

  constructor() {
    this.texture = makeBlobTexture();
    this.geometry = new THREE.PlaneGeometry(QUAD_SIZE, QUAD_SIZE).rotateX(-Math.PI / 2);
  }

  sync(entities: ReadonlyArray<Entity>, swarm: SwarmManager): void {
    let i = 0;

    for (const e of entities) {
      if (e.type !== 'enemy' || !e.alive) continue;
      const en = e as Enemy;
      if (en.radarTimer <= 0 || en.visAlpha > 0) continue;
      const elapsed = RADAR_BLIP_DURATION - en.radarTimer;
      const t = elapsed / RADAR_BLIP_DURATION;
      const alpha = 1 - t * t;
      this.placeBlob(i++, en.x, en.y, alpha);
    }

    for (let s = 0; s < swarm.alive.length; s++) {
      if (!swarm.alive[s] || swarm.radarTimer[s]! <= 0 || swarm.visAlpha[s]! > 0) continue;
      const elapsed = RADAR_BLIP_DURATION - swarm.radarTimer[s]!;
      const t = elapsed / RADAR_BLIP_DURATION;
      const alpha = 1 - t * t;
      this.placeBlob(i++, swarm.x[s]!, swarm.y[s]!, alpha);
    }

    // Hide any leftover meshes from previous frame
    for (let n = i; n < this.active; n++) {
      this.pool[n]!.mesh.visible = false;
    }
    this.active = i;
  }

  private placeBlob(index: number, tileX: number, tileY: number, alpha: number): void {
    let entry = this.pool[index];
    if (!entry) {
      const material = new THREE.MeshBasicMaterial({
        map: this.texture,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      });
      const mesh = new THREE.Mesh(this.geometry, material);
      mesh.renderOrder = 4;
      this.group.add(mesh);
      entry = { mesh, material };
      this.pool[index] = entry;
    }
    entry.mesh.visible = true;
    entry.mesh.position.set(tileX * TILE_SIZE, Y_OFFSET, tileY * TILE_SIZE);
    entry.material.opacity = Math.max(0, Math.min(1, alpha));
  }

  dispose(): void {
    for (const entry of this.pool) {
      this.group.remove(entry.mesh);
      entry.material.dispose();
    }
    this.pool.length = 0;
    this.geometry.dispose();
    this.texture.dispose();
  }
}
